import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Flare, Schedule, Whatshot, SyncAlt, Handshake,
    Fence, CenterFocusWeak, Cloud, VisibilityOff,
} from '@mui/icons-material';
import { PlayerMatch } from '../../models/PlayerMatch';
import { Team } from '../../models/Team';
import TeamLogo from '../../components/TeamLogo';
import {
    Side, getSideStats, fmtR3, legacyRating, playerName, readSubs, subsForSide, clutchTiers, bestTraderFor,
} from './matchStats';
import styles from './MatchPage2.module.css';

interface Match2DetailedProps {
    team1: Team;
    team2: Team;
    team1Rows: PlayerMatch[]; // sorted
    team2Rows: PlayerMatch[]; // sorted
    side: Side;
    selectedSteamId: string | null;
    onSelect: (steamId: string) => void;
}

// Rating 3.0 breakdown bar: deviation from 1.00 clamped to ±0.6, drawn from the
// center line (green right when above average, red left when below).
const SubBar: React.FC<{ label: string; value: number | null | undefined }> = ({ label, value }) => {
    let fill = 0;
    let offset = 50;
    let good = true;
    if (value != null) {
        const dev = Math.max(-0.6, Math.min(0.6, value - 1));
        fill = (Math.abs(dev) / 0.6) * 50;
        good = dev >= 0;
        offset = good ? 50 : 50 - fill;
    }
    return (
        <div>
            <div className={styles.subRowHead}>
                <span className={styles.subLabel}>{label}</span>
                <span className={`${styles.subVal} ${value != null ? (good ? styles.good : styles.bad) : ''}`}>
                    {value != null ? value.toFixed(2) : '–'}
                </span>
            </div>
            <div className={styles.subBar}>
                <div className={styles.subBarCenter} />
                <div
                    className={`${styles.subBarFill} ${good ? styles.subBarGood : styles.subBarBad}`}
                    style={{ width: `${fill}%`, marginLeft: `${offset}%` }}
                />
            </div>
        </div>
    );
};

const BigStat: React.FC<{ value: React.ReactNode; label: string }> = ({ value, label }) => (
    <div>
        <div className={styles.bigStatVal}>{value}</div>
        <div className={styles.bigStatLabel}>{label}</div>
    </div>
);

interface TileProps { icon: React.ReactNode; bg: string; color: string; value: React.ReactNode; label: string; }

const Tile: React.FC<TileProps> = ({ icon, bg, color, value, label }) => (
    <div className={styles.tile}>
        <span className={styles.tileIcon} style={{ background: bg, color }}>{icon}</span>
        <div>
            <div className={styles.tileVal}>{value}</div>
            <div className={styles.tileLabel}>{label}</div>
        </div>
    </div>
);

// Detailed tab: player pill selector + R3 breakdown, duels & rounds, utility & signature.
const Match2Detailed: React.FC<Match2DetailedProps> = ({
    team1, team2, team1Rows, team2Rows, side, selectedSteamId, onSelect,
}) => {
    const { t } = useTranslation('pages');
    const { t: ts } = useTranslation('stats');

    const allRows = [
        ...team1Rows.map(p => ({ p, team: team1 })),
        ...team2Rows.map(p => ({ p, team: team2 })),
    ];
    const selectedEntry = allRows.find(x => x.p.steamId === selectedSteamId) ?? allRows[0];
    if (!selectedEntry) return null;
    const player = selectedEntry.p;
    const teammates = (team1Rows.some(r => r.steamId === player.steamId) ? team1Rows : team2Rows);

    const s = getSideStats(player, side);
    const subs = subsForSide(readSubs(player), side);

    // Pre-R3 matches: no rating30 and no sub-components — show the legacy Rating card
    // (Rating 2.1/2.0 + its real inputs) instead of the empty R3 breakdown.
    const hasR3 = s.rating30 != null || subs != null;
    const legacy = legacyRating(s);
    const pm = (s.kills != null || s.deaths != null)
        ? Math.round((s.kills ?? 0) - (s.deaths ?? 0))
        : null;
    const pmStr = pm == null ? '–' : (pm > 0 ? `+${pm}` : `${pm}`);
    // Legacy Rating card: label the big number by which rating it actually is (2.1
    // preferred, else 2.0), and don't repeat that rating in the stat row below.
    const hasR21 = s.rating21 != null;
    const bigRatingLabel = hasR21 ? ts('rating21') : ts('rating2');
    const legacyStats: { v: React.ReactNode; l: string }[] = [
        ...(hasR21 && s.rating2 != null ? [{ v: fmtR3(s.rating2), l: ts('rating2') }] : []),
        { v: s.kast != null ? `${Math.round(s.kast)}%` : '–', l: ts('kast') },
        { v: s.adr != null ? s.adr.toFixed(1) : '–', l: ts('adr') },
        { v: pmStr, l: t('match2.plusMinus') },
    ];

    // Opening duels (side-aware)
    const fk = s.fk ?? 0;
    const fd = s.firstDeaths ?? 0;
    const winrate = fk + fd > 0 ? `${Math.round((fk / (fk + fd)) * 100)}%` : '–';

    // Trade %: share of deaths that got traded back (side-aware; guard divide-by-zero)
    const tradePct = s.tradedDeaths != null && s.deaths != null && s.deaths > 0
        ? `${Math.round((s.tradedDeaths / s.deaths) * 100)}%`
        : '–';
    const bestTrader = bestTraderFor(player, teammates);

    return (
        <div className={styles.fadeIn} style={{ marginTop: 18 }}>
            <div className={styles.playerPills}>
                {allRows.map(({ p, team }) => (
                    <button
                        key={p.steamId}
                        className={`${styles.playerPill} ${p.steamId === player.steamId ? styles.playerPillActive : ''}`}
                        onClick={() => onSelect(p.steamId)}
                    >
                        <TeamLogo logo={team.teamLogo} name={team.teamName} className={styles.playerPillLogo} />
                        {playerName(p)}
                    </button>
                ))}
            </div>

            <div className={styles.detailGrid}>
                {/* Rating 3.0 breakdown — or the legacy Rating card for pre-R3 matches */}
                {hasR3 ? (
                    <div className={styles.card}>
                        <div className={styles.cardTitleRow}>
                            <h3 className={styles.cardTitle}>{t('match2.r3Breakdown')}</h3>
                            <span
                                className={`${styles.cardBigValue} ${s.rating30 != null ? (s.rating30 >= 1.0 ? styles.good : styles.bad) : ''}`}
                            >
                                {fmtR3(s.rating30)}
                            </span>
                        </div>
                        <p className={styles.cardSub}>{t('match2.r3BreakdownSub')}</p>
                        <div className={styles.subList}>
                            <SubBar label={t('match2.subK')} value={subs?.k} />
                            <SubBar label={t('match2.subD')} value={subs?.d} />
                            <SubBar label={t('match2.subKast')} value={subs?.kast} />
                            <SubBar label={t('match2.subMk')} value={subs?.mk} />
                            <SubBar label={t('match2.subSw')} value={subs?.sw} />
                            <SubBar label={t('match2.subAdr')} value={subs?.adr} />
                        </div>
                    </div>
                ) : (
                    <div className={styles.card}>
                        <div className={styles.cardTitleRow}>
                            <h3 className={styles.cardTitle}>{bigRatingLabel}</h3>
                            <span
                                className={`${styles.cardBigValue} ${legacy != null ? (legacy >= 1.0 ? styles.good : styles.bad) : ''}`}
                            >
                                {fmtR3(legacy)}
                            </span>
                        </div>
                        <div className={legacyStats.length >= 4 ? styles.statGrid4 : styles.statGrid3} style={{ marginTop: 16 }}>
                            {legacyStats.map(st => (
                                <BigStat key={st.l} value={st.v} label={st.l} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Duels & rounds */}
                <div className={styles.duelsCard}>
                    <h3 className={styles.cardTitle}>{t('match2.duelsRounds')}</h3>

                    <div className={styles.sectionLabel}>{t('match2.openingDuels')}</div>
                    <div className={styles.statGrid3}>
                        <BigStat value={fk} label={t('match2.firstKills')} />
                        <BigStat value={fd} label={t('match2.firstDeaths')} />
                        <BigStat value={winrate} label={t('match2.openingWinrate')} />
                    </div>

                    <div className={styles.cardDivider} />

                    {/* Multi-kill rounds only exist at map level — shown unchanged under T/CT */}
                    <div className={styles.sectionLabel}>{t('match2.multiKillRounds')}</div>
                    <div className={styles.statGrid4}>
                        <BigStat value={player.twoKillRounds ?? 0} label="2k" />
                        <BigStat value={player.threeKillRounds ?? 0} label="3k" />
                        <BigStat value={player.fourKillRounds ?? 0} label="4k" />
                        <BigStat value={player.fiveKillRounds ?? 0} label="5k" />
                    </div>

                    <div className={styles.cardDivider} />

                    <div className={styles.sectionLabel}>{t('match2.clutches')}</div>
                    <div className={styles.statGrid5}>
                        {clutchTiers(player).map(c => (
                            <BigStat key={c.label} value={`${c.wins}-${c.attempts}`} label={c.label} />
                        ))}
                    </div>
                </div>

                {/* Utility & signature */}
                <div className={`${styles.card} ${styles.utilityCard}`}>
                    <h3 className={`${styles.cardTitle} ${styles.utilityTitle}`}>{t('match2.utilitySignature')}</h3>
                    <div className={styles.utilityGrid}>
                        <Tile
                            icon={<Flare sx={{ fontSize: 20 }} />} bg="rgba(241,196,15,0.15)" color="#f1c40f"
                            value={Math.round(s.enemiesFlashed ?? 0)} label={t('match2.enemiesFlashed')}
                        />
                        <Tile
                            icon={<Schedule sx={{ fontSize: 20 }} />} bg="rgba(74,163,255,0.15)" color="#4aa3ff"
                            value={`${(s.enemyFlashTime ?? 0).toFixed(1)}s`} label={t('match2.flashDuration')}
                        />
                        <Tile
                            icon={<Whatshot sx={{ fontSize: 20 }} />} bg="rgba(241,92,37,0.15)" color="#f15c25"
                            value={Math.round(s.utilityDamage ?? 0)} label={t('match2.utilityDamage')}
                        />
                        {s.tradedDeaths != null && (
                            <Tile
                                icon={<SyncAlt sx={{ fontSize: 20 }} />} bg="rgba(39,174,96,0.15)" color="#4ade80"
                                value={tradePct} label={t('match2.tradePct')}
                            />
                        )}
                        {bestTrader && (
                            <Tile
                                icon={<Handshake sx={{ fontSize: 20 }} />} bg="rgba(241,92,37,0.15)" color="#f15c25"
                                value={playerName(bestTrader)} label={t('match2.tradesYouMost')}
                            />
                        )}
                        {(player.wallbangKills ?? 0) > 0 && (
                            <Tile
                                icon={<Fence sx={{ fontSize: 20 }} />} bg="rgba(155,89,182,0.15)" color="#9b59b6"
                                value={player.wallbangKills} label={t('match2.wallbangKills')}
                            />
                        )}
                        {(player.noscopeKills ?? 0) > 0 && (
                            <Tile
                                icon={<CenterFocusWeak sx={{ fontSize: 20 }} />} bg="rgba(52,152,219,0.15)" color="#3498db"
                                value={player.noscopeKills} label={t('match2.noscopeKills')}
                            />
                        )}
                        {(player.smokeKills ?? 0) > 0 && (
                            <Tile
                                icon={<Cloud sx={{ fontSize: 20 }} />} bg="rgba(149,165,166,0.15)" color="#95a5a6"
                                value={player.smokeKills} label={t('match2.smokeKills')}
                            />
                        )}
                        {(player.blindKills ?? 0) > 0 && (
                            <Tile
                                icon={<VisibilityOff sx={{ fontSize: 20 }} />} bg="rgba(241,196,15,0.15)" color="#f1c40f"
                                value={player.blindKills} label={t('match2.blindKills')}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Match2Detailed;
